import {
  Button,
  confirm,
  createSortableColumn,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type TanstackReactTable,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useCreateFormCategoryMutation,
  useDeleteFormCategoryMutation,
  useFormCategoriesListQuery,
  usePatchFormCategoryMutation,
} from "@/modules/forms/presentation/api/queries";
import type { FormCategoryDTO } from "@/modules/forms/presentation/api/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";

type EditorState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: FormCategoryDTO };

export function FormCategoriesPage() {
  const nav = useNavigate();
  const canManage = useActionPermission("forms:update");
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const list = useFormCategoriesListQuery({ limit, offset });
  const createMut = useCreateFormCategoryMutation();
  const patchMut = usePatchFormCategoryMutation();
  const deleteMut = useDeleteFormCategoryMutation();

  const [editor, setEditor] = useState<EditorState>({ mode: "closed" });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");

  const openCreate = useCallback(() => {
    setName("");
    setDescription("");
    setStatus("active");
    setEditor({ mode: "create" });
  }, []);

  const openEdit = useCallback((row: FormCategoryDTO) => {
    setName(row.name);
    setDescription(row.description ?? "");
    setStatus(row.status);
    setEditor({ mode: "edit", row });
  }, []);

  const closeEditor = useCallback(() => setEditor({ mode: "closed" }), []);

  const saveEditor = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("ປ້ອນຊື່ຫມວດຫມູ່");
      return;
    }
    const body = {
      name: trimmed,
      description: description.trim().length ? description.trim() : null,
      status,
    };
    if (editor.mode === "create") {
      toast.promise(createMut.mutateAsync(body), {
        loading: "ກຳລັງບັນທຶກ...",
        success: () => {
          closeEditor();
          return "ສ້າງຫມວດຫມູ່ແລ້ວ";
        },
        error: "ບັນທຶກບໍ່ສຳເລັດ",
      });
      return;
    }
    if (editor.mode === "edit") {
      toast.promise(patchMut.mutateAsync({ id: editor.row.id, body }), {
        loading: "ກຳລັງບັນທຶກ...",
        success: () => {
          closeEditor();
          return "ອັບເດດແລ້ວ";
        },
        error: "ບັນທຶກບໍ່ສຳເລັດ",
      });
    }
  }, [closeEditor, createMut, description, editor, name, patchMut, status]);

  const columns: TanstackReactTable.ColumnDef<FormCategoryDTO>[] = [
    {
      ...createSortableColumn<FormCategoryDTO>("name", "ຊື່"),
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      enableSorting: false,
      cell: ({ row }) => (row.original.status === "active" ? "ໃຊ້ງານ" : "ປິດໃຊ້"),
    },
    {
      id: "description",
      header: "ອະທິບາຍ",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-md text-muted-foreground text-sm">
          {row.original.description?.trim() || "—"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        if (!canManage) return null;
        const c = row.original;
        return (
          <RowActions
            maxInline={2}
            actions={[
              { label: "ແກ້ໄຂ", onClick: () => openEdit(c) },
              {
                label: "ລຶບ",
                variant: "destructive",
                onClick: async () => {
                  const ok = await confirm({
                    title: "ລຶບຫມວດຫມູ່",
                    description: "ຟອມທີ່ຜູກຫມວດນີ້ຈະກັບໄປບໍ່ມີຫມວດ — ບໍ່ລຶບຟອມ.",
                    actionText: "ລຶບ",
                    ActionProps: { variant: "destructive" },
                  });
                  if (ok) {
                    toast.promise(deleteMut.mutateAsync(c.id), {
                      loading: "ກຳລັງລຶບ...",
                      success: "ລຶບແລ້ວ",
                      error: "ລຶບບໍ່ສຳເລັດ",
                    });
                  }
                },
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຟອມ", to: "/app/forms/dashboard" },
            { label: "ຟອມ", to: "/app/forms" },
            { label: "ຫມວດຫມູ່" },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-semibold text-2xl tracking-tight">
                ຫມວດຫມູ່ຟອມ
              </h1>
              <p className="text-muted-foreground text-sm">
                ຈັດກຸ່ມຟອມໃນລາຍການ ແລະ ຕອນແກ້ໄຂຟອມ
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/forms" })}
              >
                ກັບລາຍການຟອມ
              </Button>
              {canManage ? (
                <Button type="button" onClick={openCreate}>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  ຫມວດຫມູ່ໃໝ່
                </Button>
              ) : null}
            </div>
          </div>

          <div className="rounded-xl border bg-card">
            <DataTable<FormCategoryDTO, unknown>
              noDataMessage="ຍັງບໍ່ມີຫມວດຫມູ່"
              isLoading={list.isLoading}
              columns={columns}
              data={list.data?.data ?? []}
              offset={offset}
              limit={limit}
              totalCount={list.data?.meta.total ?? 0}
              sortBy="name"
              sortOrder="asc"
              onPaginationChange={(p) => setOffset(p.offset)}
              onSortingChange={() => {}}
            />
          </div>
        </div>
      </Main>

      <Dialog
        open={editor.mode !== "closed"}
        onOpenChange={(open) => !open && closeEditor()}
      >
        <DialogContent showCloseButton className="gap-4 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editor.mode === "edit" ? "ແກ້ໄຂຫມວດຫມູ່" : "ຫມວດຫມູ່ໃໝ່"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="cat-name">ຊື່</Label>
              <Input
                id="cat-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="cat-desc">ອະທິບາຍ</Label>
              <Textarea
                id="cat-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="cat-status">ສະຖານະ</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as "active" | "inactive")}
              >
                <SelectTrigger id="cat-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">ໃຊ້ງານ</SelectItem>
                  <SelectItem value="inactive">ປິດໃຊ້</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeEditor}>
              ຍົກເລີກ
            </Button>
            <Button type="button" onClick={() => saveEditor()}>
              ບັນທຶກ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
